import React, { useEffect } from "react";
import { Router, Route, Switch } from "react-router-dom";
import { Container } from "reactstrap";

import Loading from "./components/Loading";
import NavBar from "./components/NavBar";
import Footer from "./components/Footer";
import Home from "./views/Home";
import Profile from "./views/Profile";
import Settings from "./views/Settings";
import Organisation from "./views/Organisation";
import Organisations from "./views/Organisations";
import Receipts from "./views/Receipts";
import CreateOrganisation from "./views/CreateOrganisation";
import Suppliers from "./views/Suppliers";
import Embed from "./views/Embed";
import UserStringCapture from "./components/UserStringCapture";
import { useAuth0 } from "@auth0/auth0-react";
import history from "./utils/history";
import { setLoginPolicyHandler } from "./utils/apiService";

// styles
import "./App.css";

// fontawesome
import initFontAwesome from "./utils/initFontAwesome";
initFontAwesome();

const MFA_ACR = "http://schemas.openid.net/pape/policies/2007/06/multi-factor";
const LOGIN_POLICY_RETRY_KEY = "billsmax.loginPolicyRedirect";

/**
 * When the API says the selected org needs MFA or SSO, log in again asking Auth0 for it. A second ask for the same
 * thing within two minutes means the new login did not satisfy it either, so it is left to show as an error rather
 * than redirecting in a loop.
 */
const redirectForLoginPolicy = (code, loginWithRedirect, email) => {
  let last = null;
  try { last = JSON.parse(sessionStorage.getItem(LOGIN_POLICY_RETRY_KEY)); } catch (e) {}
  if (last && last.code === code && Date.now() - last.at < 2 * 60 * 1000) return;
  try { sessionStorage.setItem(LOGIN_POLICY_RETRY_KEY, JSON.stringify({ code, at: Date.now() })); } catch (e) {}

  loginWithRedirect({
    appState: { returnTo: window.location.pathname },
    authorizationParams: code === "login_mfa_required"
      ? { acr_values: MFA_ACR }
      : { prompt: "login", ...(email ? { login_hint: email } : null) },
  });
};

const App = () => {
  const { isLoading, error, isAuthenticated, getAccessTokenSilently, loginWithPopup, loginWithRedirect, user } = useAuth0();

  useEffect(() => {
    setLoginPolicyHandler((code) => redirectForLoginPolicy(code, loginWithRedirect, user?.email));
    return () => setLoginPolicyHandler(null);
  }, [loginWithRedirect, user]);

  // Use only SPA JS authentication - no Universal Login redirects
  useEffect(() => {
    console.log('App: Authentication state changed:', { isLoading, isAuthenticated, error });
    
    if (!isLoading && !isAuthenticated) {
      console.log('App: Attempting silent authentication...');
      // Try silent authentication - this happens entirely on your domain
      getAccessTokenSilently({ ignoreCache: false })
        .then((token) => {
          console.log('App: Silent authentication succeeded, token length:', token?.length);
        })
        .catch((error) => {
          console.log('App: Silent authentication failed:', error);
          // Note: postMessage is now handled by UserStringCapture component via shared service
        });
    }
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  if (error) {
    console.log('App: Auth error:', error);
    return <div>Oops... {error.message}</div>;
  }

  return (
    <Router history={history}>
      <Switch>
        {/* Embed route - always render UserStringCapture to keep timer running */}
        <Route path="/embed" render={() => (
          <UserStringCapture isAuthenticated={isAuthenticated} />
        )} />
        
        {/* All other routes require authentication */}
        <Route path="*">
          {isLoading || !isAuthenticated ? (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
              <div className="text-center" style={{ maxWidth: '600px', padding: '2rem' }}>
                <img 
                  className="mb-3" 
                  src="/Logo.png" 
                  alt="BillsMax Logo" 
                  style={{ width: '300px', height: 'auto', maxWidth: '100%' }}
                />
                <h1 className="mb-3" style={{ color: '#343a40', fontWeight: '600' }}></h1>
                <p className="lead mb-4" style={{ color: '#6c757d', maxWidth: '500px', margin: '0 auto' }}>
                  Receipt management software
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center mb-4">
                  <button 
                    className="btn btn-primary btn-lg px-4 py-2"
                    onClick={() => {
                      console.log('App: Login popup clicked');
                      loginWithPopup();
                    }}
                    style={{ minWidth: '160px' }}
                  >
                    Login
                  </button>
                  {/* <button 
                    className="btn btn-outline-primary btn-lg px-4 py-2"
                    onClick={() => {
                      console.log('App: Login redirect clicked');
                      loginWithRedirect();
                    }}
                    style={{ minWidth: '160px' }}
                  >
                    Login with Redirect
                  </button> */}
                </div>
              </div>
            </div>
          ) : (
            <div id="app" className="d-flex flex-column h-100">
              <NavBar />
              <Container className="flex-grow-1 mt-5">
                <Switch>
                  <Route path="/" exact component={Home} />
                  <Route path="/profile" component={Profile} />
                  <Route path="/settings" component={Settings} />
                  <Route path={["/team/:orgId", "/organisation/:orgId"]} component={Organisation} />
                  <Route path={["/teams", "/organisations"]} exact component={Organisations} />
                  <Route path={["/teams/create", "/organisations/create"]} component={CreateOrganisation} />
                  <Route path="/receipts" exact component={Receipts} />
                  <Route path="/suppliers/:uploadType" component={Suppliers} />
                </Switch>
              </Container>
              <Footer />
            </div>
          )}
        </Route>
      </Switch>
    </Router>
  );
};

export default App;
